import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, ExternalLink, Package, BookmarkPlus, Sparkles, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { ProductPickerDialog } from "../ProductPickerDialog";

const CATEGORIES = ["Furniture", "Fixtures", "Equipment", "Lighting", "Textiles", "Decor", "Other"];
const STATUSES = ["Specified", "Approved", "Ordered", "Shipped", "Delivered", "Installed"];
const STATUS_COLORS = {
  Specified: "#A68A6B", Approved: "#8C7154", Ordered: "#C49A4A",
  Shipped: "#5C7A8C", Delivered: "#506B52", Installed: "#3f553f",
};

const EMPTY = { name: "", category: "Furniture", room: "", vendor: "", quantity: 1, unit_price: "", status: "Specified", lead_time: "", link: "", image_url: "", notes: "" };

export default function FFETab({ projectId, readOnly }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [smartUrl, setSmartUrl] = useState("");
  const [smartFetching, setSmartFetching] = useState(false);

  const smartImport = async () => {
    const url = smartUrl.trim();
    if (!url) { toast.error("Paste a product URL first"); return; }
    setSmartFetching(true);
    try {
      const { data } = await api.post("/products/smart-fill", { url });
      setForm((s) => ({
        ...s,
        name: data.name || s.name,
        vendor: data.vendor || s.vendor,
        unit_price: data.price ? String(data.price) : s.unit_price,
        link: data.link || url,
        image_url: data.image_url || s.image_url,
      }));
      const missing = [];
      if (!data.image_url) missing.push("photo");
      if (!data.price) missing.push("price");
      if (missing.length) toast.warning(`Imported — couldn't auto-detect ${missing.join(" & ")}. Please confirm details before adding.`);
      else toast.success("Imported — please confirm details before adding");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSmartFetching(false); }
  };

  const applyProduct = (p) => {
    setForm((s) => ({
      ...s,
      name: p.name || s.name,
      vendor: p.vendor || s.vendor,
      unit_price: p.price ? String(p.price) : s.unit_price,
      link: p.link || s.link,
      category: CATEGORIES.includes(p.category) ? p.category : s.category,
      lead_time: p.lead_time || s.lead_time,
      image_url: p.image_url || s.image_url,
      notes: p.notes || s.notes,
    }));
  };

  const saveToLibrary = async (i) => {
    try {
      const fd = new FormData();
      fd.append("name", i.name || "");
      fd.append("vendor", i.vendor || "");
      fd.append("price", String(i.unit_price || 0));
      fd.append("category", i.category || "");
      fd.append("lead_time", i.lead_time || "");
      fd.append("link", i.link || "");
      fd.append("image_url", i.image_url || "");
      fd.append("notes", i.notes || "");
      await api.post("/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Saved to Product Library");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const load = () => api.get(`/projects/${projectId}/ffe`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/ffe`, {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        unit_price: parseFloat(form.unit_price) || 0,
      });
      toast.success("Item added");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/ffe/${id}`, { status });
    load();
  };
  const remove = async (id) => { await api.delete(`/ffe/${id}`); load(); };

  const grandTotal = useMemo(() => items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0), [items]);
  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="max-w-6xl space-y-6" data-testid="ffe-tab">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Schedule</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">FF&amp;E</h3>
          <p className="text-sm text-[#5C5651] mt-1">Furniture, Fixtures &amp; Equipment</p>
        </div>
        <div className="flex items-end gap-6">
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Total</p>
            <p data-testid="ffe-grand-total" className="font-serif text-3xl text-[#2B2622]">{fmt(grandTotal)}</p>
          </div>
          {!readOnly && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setSmartUrl(""); } }}>
              <DialogTrigger asChild>
                <Button data-testid="new-ffe-btn"
                  className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
                  <Plus size={15} className="mr-2" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2B2622]">Add FF&amp;E item</DialogTitle><DialogDescription className="sr-only">Add a furniture, fixture or equipment item</DialogDescription></DialogHeader>
                <form onSubmit={add} className="space-y-4 mt-2">
                  <div className="border border-[#A68A6B] bg-[#F9F6F1] p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={15} className="text-[#A68A6B]" />
                      <span className="text-xs uppercase tracking-wider text-[#8C7154]">Smart import</span>
                    </div>
                    <p className="text-[12px] text-[#5C5651] mb-2">Paste a vendor product link to auto-fill the photo, name, vendor &amp; price.</p>
                    <div className="flex gap-2">
                      <Input data-testid="ffe-smart-url-input" type="url" value={smartUrl}
                        onChange={(e) => setSmartUrl(e.target.value)} placeholder="https://www.vendor.com/product…"
                        className="flex-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                      <Button data-testid="ffe-smart-fetch-btn" type="button" onClick={smartImport} disabled={smartFetching}
                        className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs px-4">
                        <Wand2 size={14} className="mr-1.5" /> {smartFetching ? "Fetching…" : "Fetch"}
                      </Button>
                    </div>
                  </div>
                  <Button data-testid="ffe-pick-product-btn" type="button" variant="outline" onClick={() => setPickerOpen(true)}
                    className="w-full rounded-none border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] uppercase tracking-wider text-xs py-4">
                    <Package size={14} className="mr-2" /> Choose from Product Library
                  </Button>
                  {form.image_url && (
                    <div className="relative w-full">
                      <img src={form.image_url} alt="product" data-testid="ffe-image-preview"
                        className="w-full max-h-48 object-cover border border-[#EBE5E0]" />
                      <button type="button" onClick={() => setForm({ ...form, image_url: "" })}
                        className="absolute top-2 right-2 bg-white/90 p-1 text-[#9E4747]"><X size={14} /></button>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Item name</Label>
                    <Input data-testid="ffe-name-input" required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="ffe-category-input" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Room / Location</Label>
                      <Input data-testid="ffe-room-input" value={form.room}
                        onChange={(e) => setForm({ ...form, room: e.target.value })}
                        className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Vendor / Supplier</Label>
                    <Input data-testid="ffe-vendor-input" value={form.vendor}
                      onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                      className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Qty</Label>
                      <Input data-testid="ffe-qty-input" type="number" min="1" value={form.quantity}
                        onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                        className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Unit price</Label>
                      <Input data-testid="ffe-price-input" type="number" step="0.01" value={form.unit_price}
                        onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                        className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid="ffe-status-input" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Lead time (optional)</Label>
                    <Input data-testid="ffe-lead-time-input" placeholder="e.g. 6–8 weeks" value={form.lead_time}
                      onChange={(e) => setForm({ ...form, lead_time: e.target.value })}
                      className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Product link (optional)</Label>
                    <Input data-testid="ffe-link-input" type="url" placeholder="https://…" value={form.link}
                      onChange={(e) => setForm({ ...form, link: e.target.value })}
                      className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Notes</Label>
                    <Textarea data-testid="ffe-notes-input" value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  </div>
                  <Button data-testid="add-ffe-submit" type="submit" disabled={busy}
                    className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                    {busy ? "Adding…" : "Add Item"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          No FF&amp;E items yet.
        </div>
      ) : (
        <div className="border border-[#EBE5E0] bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-[#EBE5E0] text-left text-[11px] uppercase tracking-wider text-[#A68A6B]">
                <th className="p-4 font-medium">Item</th>
                <th className="p-4 font-medium">Category</th>
                <th className="p-4 font-medium">Room</th>
                <th className="p-4 font-medium">Vendor</th>
                <th className="p-4 font-medium">Lead Time</th>
                <th className="p-4 font-medium text-right">Qty</th>
                <th className="p-4 font-medium text-right">Unit</th>
                <th className="p-4 font-medium text-right">Total</th>
                <th className="p-4 font-medium">Status</th>
                {!readOnly && <th className="p-4 w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const total = (i.quantity || 0) * (i.unit_price || 0);
                return (
                  <tr key={i.id} className="border-b border-[#F2EFEB]" data-testid="ffe-row">
                    <td className="p-4 text-[#2B2622]">
                      <div className="flex items-center gap-3">
                        {i.image_url && (
                          <img src={i.image_url} alt={i.name} data-testid={`ffe-thumb-${i.id}`}
                            className="w-10 h-10 object-cover border border-[#EBE5E0] shrink-0" />
                        )}
                        <div>
                          <div className="flex items-center gap-1.5">
                            {i.name}
                            {i.link && (
                              <a href={i.link} target="_blank" rel="noopener noreferrer" data-testid={`ffe-link-${i.id}`}
                                className="text-[#A68A6B] hover:text-[#8C7154]"><ExternalLink size={13} /></a>
                            )}
                          </div>
                          {i.notes && <p className="text-[11px] text-[#9b938b] mt-0.5">{i.notes}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-[#5C5651]">{i.category}</td>
                    <td className="p-4 text-[#5C5651]">{i.room || "—"}</td>
                    <td className="p-4 text-[#5C5651]">{i.vendor || "—"}</td>
                    <td className="p-4 text-[#5C5651]" data-testid={`ffe-lead-time-${i.id}`}>{i.lead_time || "—"}</td>
                    <td className="p-4 text-right text-[#2B2622]">{i.quantity}</td>
                    <td className="p-4 text-right text-[#2B2622]">{fmt(i.unit_price)}</td>
                    <td className="p-4 text-right text-[#2B2622] font-medium">{fmt(total)}</td>
                    <td className="p-4">
                      {readOnly ? (
                        <span className="text-xs uppercase tracking-wider" style={{ color: STATUS_COLORS[i.status] || "#A68A6B" }}>{i.status}</span>
                      ) : (
                        <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                          <SelectTrigger data-testid={`ffe-status-${i.id}`} className="h-8 rounded-none border-[#EBE5E0] focus:ring-0 text-xs uppercase tracking-wider"
                            style={{ color: STATUS_COLORS[i.status] || "#A68A6B" }}><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </td>
                    {!readOnly && (
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`save-ffe-to-library-${i.id}`} onClick={() => saveToLibrary(i)}
                            title="Save to Product Library"
                            className="text-[#5C5651] hover:text-[#A68A6B]"><BookmarkPlus size={15} strokeWidth={1.5} /></button>
                          <button data-testid={`delete-ffe-${i.id}`} onClick={() => remove(i.id)}
                            className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              <tr className="bg-[#F9F8F6] font-medium">
                <td className="p-4 text-[#2B2622] uppercase text-xs tracking-wider" colSpan={7}>Grand Total</td>
                <td className="p-4 text-right text-[#2B2622]">{fmt(grandTotal)}</td>
                <td colSpan={readOnly ? 1 : 2}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <ProductPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onSelect={applyProduct} />
    </div>
  );
}
